import ReactECharts from 'echarts-for-react';
import ragData from '../data/ragRetrievalBenchmark.json';

/**
 * RetrievalLatencyChart — average latency by retrieval depth k (Fig 2).
 * Shows the cold-start cliff (k=3 pays an unwarmed embedding call), not a
 * monotonic cost-of-depth curve.
 */

const BRAND = { warning: '#F59E0B', orange: '#FD9800' };

type KItem = { k: number; latencyMs: number };

const fmtMs = (v: number) => `${v.toFixed(0)}ms`;

export const RetrievalLatencyChart = ({ height = 300 }: { height?: number }) => {
  const fig = (
    ragData as unknown as { figures: { retrieval_by_k: { items: KItem[] } } }
  ).figures.retrieval_by_k;

  const option = {
    backgroundColor: '#171717',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (params: { marker?: string; name: string; value: number }[]) =>
        params.map((p) => `${p.marker} ${p.name}: <b>${fmtMs(p.value)}</b>`).join('<br/>'),
    },
    grid: { left: 56, right: 24, top: 32, bottom: 40 },
    xAxis: {
      type: 'category',
      data: fig.items.map((i) => `k=${i.k}`),
      axisLine: { lineStyle: { color: '#333' } },
      axisLabel: { color: '#e5e5e5', fontSize: 13, fontWeight: 700 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'avg latency (ms)',
      nameTextStyle: { color: '#9CA3AF', fontSize: 11 },
      axisLabel: { color: '#9CA3AF', fontSize: 12, formatter: (v: number) => fmtMs(v) },
      splitLine: { lineStyle: { color: '#333', opacity: 0.5 } },
    },
    series: [
      {
        name: 'Avg latency',
        type: 'bar',
        barWidth: '42%',
        data: fig.items.map((i, idx) => ({
          value: i.latencyMs,
          itemStyle: { color: idx === 0 ? BRAND.warning : BRAND.orange, borderRadius: [6, 6, 0, 0] },
        })),
        label: {
          show: true,
          position: 'top',
          color: '#e5e5e5',
          fontWeight: 700,
          fontSize: 13,
          formatter: (p: { value: number }) => fmtMs(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 2. Average latency by retrieval depth. The k=3 cold-start cost reflects an unwarmed
        embedding call on the first request of each query; subsequent requests were faster across all
        depths.
      </p>
    </div>
  );
};
